﻿/**
 * search-chats.js
 * Domain use case that normalises chat search requests and results.
 */
const DEFAULT_TITLE = 'Untitled Chat';
const DEFAULT_PROJECT_TITLE = 'Unfiled';

function defaultNormalise(result) {
  if (!result || typeof result !== 'object') {
    return {
      id: null,
      title: DEFAULT_TITLE,
      projectTitle: DEFAULT_PROJECT_TITLE,
      excerpt: '',
      raw: result ?? null,
    };
  }

  return {
    id: result.id || result.chat_id || null,
    title: toStringSafe(result.title, DEFAULT_TITLE),
    projectTitle: toStringSafe(result.project_title, DEFAULT_PROJECT_TITLE),
    excerpt: toStringSafe(result.excerpt, ''),
    raw: result,
  };
}

function toStringSafe(value, fallback = '') {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

function extractResults(payload) {
  if (Array.isArray(payload?.results)) {
    return payload.results;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
}

export function createSearchChats({ search, fallbackSearch, normaliseResult } = {}) {
  if (typeof search !== 'function') {
    throw new Error('search is required');
  }

  const normalise = typeof normaliseResult === 'function' ? normaliseResult : defaultNormalise;
  const fallback = typeof fallbackSearch === 'function' ? fallbackSearch : null;

  return async function searchChats({ query } = {}) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!trimmed) {
      return {
        results: [],
        source: 'empty',
        context: { query: '' },
      };
    }

    try {
      const payload = await search(trimmed);
      const results = extractResults(payload).map(normalise);
      return {
        results,
        source: 'service',
        context: { query: trimmed },
        response: payload ?? null,
      };
    } catch (error) {
      if (!fallback) {
        throw error;
      }
      const payload = await fallback(trimmed);
      const results = extractResults(payload).map(normalise);
      return {
        results,
        source: 'fallback',
        context: { query: trimmed },
        response: payload ?? null,
        error,
      };
    }
  };
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.search = window.AgileProDomain.search || {};
  window.AgileProDomain.search.createSearchChats = createSearchChats;
}
